import { useLocation, useNavigate } from 'react-router-dom'
import Paper from '@mui/material/Paper'
import BottomNavigation from '@mui/material/BottomNavigation'
import BottomNavigationAction from '@mui/material/BottomNavigationAction'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { isItemEntry } from 'components/core/SplitPanes'
import { useNavItems } from './NavItems'

const TAB_BAR_HEIGHT = 56

// A Home Screen web app on a phone with a home indicator (a Face ID iPhone,
// whose screen is 780pt or more on its long side) is drawn to the screen's
// foot, under the indicator, with no inset to read without viewport-fit=cover;
// the bar and the chat's screens keep this much clear of it. A Safari tab has
// its own toolbar there, and keeps none.
export const HOME_INDICATOR_SPACE =
  (window.navigator.standalone ||
    window.matchMedia('(display-mode: standalone)').matches) &&
  Math.max(window.screen.width, window.screen.height) >= 780
    ? 20
    : 0

// The room a page keeps under itself for the bar.
export const TAB_BAR_SPACE = TAB_BAR_HEIGHT + HOME_INDICATOR_SPACE

// A phone's nav in place of the drawer: the pages used most on a phone, then
// More for the rest (More.js), which is lit on any page the tabs leave out.
// A solid bar along the window's foot: Safari keeps its own toolbar below it,
// tints that toolbar from it, and the bar rides down as the toolbar
// minimizes on a scroll.
const TabBar = () => {
  const location = useLocation()
  const { pathname } = location
  const navigate = useNavigate()
  const { tabs } = useNavItems()
  const value =
    tabs.find(({ path }) => pathname.startsWith(path))?.path || '/more'

  // The open tab, tapped again, goes back to its own page from what was
  // opened under it, as iOS tabs do: a user or moment back to its list, a
  // report to the reports, a page opened from More to More. Tapped on its
  // own page, it scrolls the page to the top.
  const onChange = (event, path) => {
    if (path !== value) navigate(path)
    else if (isItemEntry(location)) navigate(-1)
    else if (pathname !== path) navigate(path)
    else window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <Paper
      square
      elevation={0}
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        borderTop: 1,
        borderColor: 'divider',
        pb: `${HOME_INDICATOR_SPACE}px`,
      }}
    >
      <BottomNavigation
        showLabels
        value={value}
        onChange={onChange}
        sx={{
          height: TAB_BAR_HEIGHT,
          bgcolor: 'background.paper',
          '& .MuiBottomNavigationAction-root': { minWidth: 0, px: 0.5 },
          // iOS keeps a tab's label one size whether or not it's open.
          '& .MuiBottomNavigationAction-label, & .MuiBottomNavigationAction-label.Mui-selected':
            { fontSize: 11 },
        }}
      >
        {tabs.map(({ path, title, Icon }) => (
          <BottomNavigationAction
            key={path}
            value={path}
            label={title}
            icon={<Icon />}
          />
        ))}
        <BottomNavigationAction
          value="/more"
          label="More"
          icon={<MoreHorizIcon />}
        />
      </BottomNavigation>
    </Paper>
  )
}

export default TabBar
